import type { CSSProperties, ReactNode } from 'react';
import { boundedTone, level, type Tone } from './state.ts';
import { NO_VALUE, unitIsTight } from './format.ts';
import { Unit } from './Unit.tsx';
import styles from './Meter.module.css';

/* ==========================================================================
   Meter — a bounded value judged against a limit: battery, disk, signal.

   Default to this. A radial Gauge (below) has to earn its place twice over:
   the value needs a hard limit AND the gauge has to sit next to the control
   the operator is using while they read it.

   ONE GEOMETRY, THREE SIZES, NO STRETCH.
   The component lays itself out as a grid — [label] · track · readout — whose
   track column is one of exactly three fixed widths (48 / 64 / 88px) and
   whose readout column is a fixed `ch` width derived from the METRIC'S BOUNDS
   (`max.toFixed(digits) + unit`), not from the digits this particular reading
   happens to carry. So `8%`, `80%` and a row with no reading at all draw
   tracks of identical length starting at an identical x, an 8% bar is exactly
   one tenth of an 80% bar, and 28 of them down a table column form one
   straight left edge and one straight right edge.

   The track is never sized by its container, and there is no opt-out. A
   meter encodes a *bounded* quantity, so the same 0–100% span has to be the
   same span of pixels in a table cell, in an identity strip and in a rail —
   otherwise the published 15% limit tick lands at a different distance from
   the origin on every surface and the operator has to re-read each bar's
   proportion instead of sweeping a column. A bar that should be as long as
   its container is a `Distribution` (proportional to a peak) or a
   `Sparkline` (a series over time); it is not a meter.

   COLOUR ANSWERS THE THRESHOLD, NOT THE CATEGORY — and a caller cannot
   overrule that. `tone` is a *floor*, honoured only while the reading is
   inside its limits (so "signal strength is merely a fact" can stay steel);
   the moment a limit is crossed the derived tone wins. A green trace beside
   a red reading of the same battery is the console lying to the operator,
   and no call site is allowed to arrange it.
   ========================================================================== */

/** Widest string the readout can ever print, in `ch`, so the column is
 *  reserved once and never jitters as the value updates. */
function readoutCh(min: number, max: number, digits: number, unit: string): number {
  const widest = Math.max(
    min.toFixed(digits).length,
    max.toFixed(digits).length,
    NO_VALUE.length,
  );
  return widest + unit.length;
}

export interface MeterProps {
  /** Null renders as a hatched empty track — never as 0 and never as green. */
  value: number | null | undefined;
  min?: number | undefined;
  max?: number | undefined;
  label?: string | undefined;
  unit?: string | undefined;
  /**
   * Thresholds in the value's own units. Low-is-bad by default (battery,
   * disk free); pass `invert` for high-is-bad (CPU, temperature).
   */
  warn?: number | undefined;
  bad?: number | undefined;
  invert?: boolean | undefined;
  /**
   * The tone to wear while the reading is INSIDE its limits — for a quantity
   * that is merely a fact (signal strength, fill level). A crossed threshold
   * always overrules it; this is a floor, not an override.
   */
  tone?: Tone | undefined;
  /**
   * A 1px tick on the track at a limit worth seeing. Defaults to `bad`, so
   * every meter publishes its hard limit instead of asking the operator to
   * know that 15 is the number. The tick is neutral rather than red on
   * purpose: the fill grows from the same edge, so "the bar has passed the
   * tick" is legible from position alone and the colour channel stays spent
   * on the fill. (`Sparkline` draws its limits in --warn/--error because a
   * trace moves in two dimensions and its rules have to be told apart.)
   * Pass `null` to suppress it.
   */
  limit?: number | null | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  digits?: number | undefined;
  /** Hide the numeric readout when the number is already elsewhere. */
  showValue?: boolean | undefined;
}

export function Meter({
  value,
  min = 0,
  max = 100,
  label,
  unit = '%',
  warn = 40,
  bad = 15,
  invert = false,
  tone,
  limit,
  size = 'md',
  digits = 0,
  showValue = true,
}: MeterProps) {
  const known = value != null && !Number.isNaN(value);
  const pctOfRange = known ? ((value - min) / (max - min)) * 100 : 0;
  const lv = level(known ? value : null, warn, bad, invert);
  const resolved: Tone = boundedTone(lv, tone);
  const width = Math.max(0, Math.min(100, pctOfRange));
  /* The published limit. `bad` unless the caller names another one, and only
     drawn where it actually falls inside the track. */
  const tick = limit === null ? undefined : limit ?? bad;
  const tickAt = tick === undefined || tick <= min || tick >= max
    ? undefined
    : ((tick - min) / (max - min)) * 100;

  return (
    <div
      className={styles.meter}
      data-tone={resolved}
      data-size={size}
      data-unknown={!known}
      data-labelled={label !== undefined}
      data-readout={showValue}
      style={{ '--meter-read': `${readoutCh(min, max, digits, unit)}ch` } as CSSProperties}
    >
      {label !== undefined && <span className={styles.label}>{label}</span>}
      <span
        className={styles.track}
        role="meter"
        aria-label={label ?? 'value'}
        aria-valuenow={known ? value : undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={known ? `${value.toFixed(digits)}${unit}` : 'no reading'}
      >
        {known && <span className={styles.fill} style={{ width: `${width}%` }} />}
        {!known && <span className={styles.fill} />}
        {tickAt !== undefined && (
          <span
            className={styles.tick}
            style={{ left: `${tickAt}%` }}
            title={`limit ${tick}${unitIsTight(unit) ? '' : ' '}${unit}`}
          />
        )}
      </span>
      {showValue && (
        <span className={styles.value} data-toned={lv !== 'ok' && lv !== 'unknown'}>
          {known ? value.toFixed(digits) : NO_VALUE}
          {known && <Unit unit={unit} />}
        </span>
      )}
    </div>
  );
}

/**
 * Stacked meters, aligned. Labels, tracks and readouts share one three-column
 * grid, so four vitals in a rail line up on three edges instead of drifting
 * with the length of their labels. Every child must be a <Meter> with a label.
 */
export function MeterList({ children }: { children: ReactNode }) {
  return <div className={styles.list}>{children}</div>;
}

/* --------------------------------------------------------------------------
   Gauge — a 270° arc. Justified only beside a control: speed and rotation
   above a D-pad, where the limit is judged while driving.

   The arc obeys the same rule as the bar: `--nominal` steel inside its
   limits, amber at `warn`, red at `bad`/past `limit`. It is deliberately NOT
   amber by default — amber is the accent, and the accent has exactly one job
   (the primary verb, the current selection, the focus ring). A speed of
   0.5 m/s is not a warning and must not be painted like one.
   -------------------------------------------------------------------------- */

/* Degrees are measured clockwise from 12 o'clock, so the 270° sweep runs
   bottom-left → top → bottom-right and the gap sits under the needle where
   an operator's eye is not. */
const START = 225;
const SWEEP = 270;

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
}

function arc(cx: number, cy: number, r: number, from: number, to: number) {
  const a = polar(cx, cy, r, from);
  const b = polar(cx, cy, r, to);
  const large = Math.abs(to - from) > 180 ? 1 : 0;
  return `M${a.x.toFixed(2)},${a.y.toFixed(2)} A${r},${r} 0 ${large} 1 ${b.x.toFixed(2)},${b.y.toFixed(2)}`;
}

export function Gauge({
  value,
  min = 0,
  max = 100,
  label,
  unit,
  limit,
  warn,
  bad,
  invert = true,
  size = 56,
  tone,
  digits = 1,
}: {
  value: number | null | undefined;
  min?: number | undefined;
  max?: number | undefined;
  label?: string | undefined;
  unit?: string | undefined;
  /** The 1px red tick on the arc, and — absent `bad` — the hard limit. */
  limit?: number | undefined;
  /** Thresholds in the value's own units. Gauges bound rates, so HIGH is bad
   *  by default; pass `invert={false}` for a gauge where low is the problem. */
  warn?: number | undefined;
  bad?: number | undefined;
  invert?: boolean | undefined;
  size?: number | undefined;
  /** Honoured only inside the limits — see `boundedTone`. */
  tone?: Tone | undefined;
  digits?: number | undefined;
}) {
  const known = value != null && !Number.isNaN(value);
  const frac = known ? Math.max(0, Math.min(1, (value - min) / (max - min))) : 0;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  const stroke = Math.max(3, Math.round(size / 14));

  /* A bare `limit` is the hard one: past it is red, and there is no amber
     band unless the caller names one. */
  const hardBad = bad ?? limit;
  const softWarn = warn ?? hardBad;
  const lv =
    hardBad === undefined || softWarn === undefined
      ? (known ? 'ok' : 'unknown')
      : level(known ? value : null, softWarn, hardBad, invert);

  return (
    <div
      className={styles.gauge}
      data-tone={boundedTone(lv, tone)}
      data-toned={lv === 'warn' || lv === 'bad'}
    >
      <svg width={size} height={size * 0.82} viewBox={`0 0 ${size} ${size * 0.82}`}
           role="img" aria-label={`${label ?? 'value'} ${known ? value.toFixed(digits) : 'unknown'}${unit ?? ''}`}>
        <path
          className={styles.gaugeTrack}
          strokeWidth={stroke}
          d={arc(cx, cy, r, START, START + SWEEP)}
        />
        {known && frac > 0 && (
          <path
            className={styles.gaugeFill}
            strokeWidth={stroke}
            d={arc(cx, cy, r, START, START + SWEEP * frac)}
          />
        )}
        {limit !== undefined && (() => {
          const deg = START + SWEEP * ((limit - min) / (max - min));
          const a = polar(cx, cy, r - stroke / 2 - 1, deg);
          const b = polar(cx, cy, r + stroke / 2 + 1, deg);
          return <line className={styles.gaugeLimit} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
        })()}
        <text className={styles.gaugeValue} x={cx} y={unit === undefined ? cy + 4 : cy + 1}>
          {known ? value.toFixed(digits) : NO_VALUE}
        </text>
        {unit !== undefined && (
          <text className={styles.gaugeUnit} x={cx} y={cy + 11}>
            {unit}
          </text>
        )}
      </svg>
      {label !== undefined && <span className={styles.gaugeLabel}>{label}</span>}
    </div>
  );
}
